import { FC, useEffect } from 'react';
import {
  Button,
  CodeEditor,
  type Extension,
  TextCaption,
  TextSubtitle,
  Stack,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '../..';

import './editor-modal.scss';

export type EditorModalProps = GenericModalProps & {
  code?: string;
  title?: string;
  langs?: Extension[];
  progress?: number;
  loading?: boolean;
  viewOnly?: boolean;
  /**
   * Plain text shown in place of the code editor (e.g. "contents withheld").
   * It lives in the dialog's own content, not in a non-focusable editor.
   */
  notice?: string;
  /**
   * Returning `false` KEEPS THE MODAL OPEN, for a save that can be rejected —
   * the JSON editor parses on save and must not discard what was typed. Any
   * other return value (including `undefined`) closes, so every existing caller
   * is unaffected.
   */
  onSave?: (code: string) => void | boolean;
  /** Shown above the footer. Pair it with an `onSave` that returns `false`. */
  error?: string;
  /**
   * Fed by the editor on every keystroke. Supplied by `useEditorModal`, which
   * owns the draft — see the note there (PSTUDIO-3897) for why the content must
   * not live inside the web component.
   */
  onCodeChange?: (code: string) => void;
};

export const EditorModal: FC<EditorModalProps> = ({
  modalRef,
  code,
  title,
  langs,
  loading,
  progress,
  viewOnly,
  notice,
  onClose,
  onSave,
  onCodeChange,
  error,
}) => {
  useEffect(() => {
    if (progress && progress >= 100 && !loading) {
      onClose?.();
    }
  }, [progress, loading, onClose]);

  // `code` IS the live draft (controlled by `useEditorModal`), so it can be
  // saved directly. Reading the web component back through a ref was how a
  // prop-driven revert used to slip into the persisted value.
  const handleSave = () => {
    if (onSave?.(code ?? '') === false) return;
    onClose?.();
  };

  return (
    <GenericModal
      test-id="editor-modal"
      modalRef={modalRef}
      size="large"
      hideCloseBtn
      stayOnClickOutside
      stayOnEsc
      expandable
      className="c-editor-modal"
    >
      <TextSubtitle size="small" slot="title">
        {title}
      </TextSubtitle>

      {notice ? (
        <p className="c-editor-modal__notice">{notice}</p>
      ) : (
        <CodeEditor
          extensions={langs}
          value={code || ''}
          onChange={onCodeChange}
          height="calc(100vh - 124px)"
          theme="vscodeDark"
          disabled={loading || viewOnly}
        />
      )}

      <Stack justifyContent="end" gutter="small" slot="footer">
        {error && (
          <TextCaption variant="danger" style={{ marginInlineEnd: 'auto' }}>
            {error}
          </TextCaption>
        )}
        <Button
          test-id="ps-editor-modal-cancel"
          size="large"
          variant="secondary"
          onClick={onClose}
          disabled={loading}
        >
          <TranslationComponent i18nKey="pro-studio/actions:editorModal.cancel">
            Cancel
          </TranslationComponent>
        </Button>
        {!viewOnly && (
          <Button
            test-id="ps-editor-modal-save"
            size="large"
            onClick={handleSave}
            disabled={loading}
          >
            <TranslationComponent
              i18nKey={
                loading && progress
                  ? 'pro-studio/actions:editorModal.save.progress'
                  : 'pro-studio/actions:editorModal.save.label'
              }
            >
              {loading ? 'Saving' : 'Save'}
            </TranslationComponent>
            {loading && progress && `: ${progress}%`}
          </Button>
        )}
      </Stack>
    </GenericModal>
  );
};
